import type { RouteRecordRaw } from 'vue-router';
import type { DiscoveredRouteModule } from './types';
import type { AppConfig } from '@/types/app-config';

function groupByScope(routes: DiscoveredRouteModule[]) {
  return {
    root: routes.filter(r => r.scope === 'root'),
    app: routes.filter(r => r.scope === 'app'),
    admin: routes.filter(r => r.scope === 'admin'),
  } as const;
}

function isFeatureAllowed(required: string | string[] | undefined, features: Record<string, boolean> | undefined): boolean {
  if (!required) return true;
  const f = features || {};
  if (Array.isArray(required)) return required.every(key => f[key] === true);
  return f[required] === true || Object.keys(f).length === 0; // allow when features not yet loaded
}

function filterByConfig(routes: DiscoveredRouteModule[], appConfig: AppConfig, features?: Record<string, boolean>): DiscoveredRouteModule[] {
  return routes.filter(r => {
    // Respect project-type specific route visibility via meta flags
    const onlyWhiteLabel = Boolean((r.meta as any)?.onlyWhiteLabel);
    const onlyPrivateLabel = Boolean((r.meta as any)?.onlyPrivateLabel);
    if (onlyWhiteLabel && appConfig.projectType !== 'white_label') return false;
    if (onlyPrivateLabel && appConfig.projectType !== 'private_label') return false;

    if (!r.moduleKey) return isFeatureAllowed(r.requiresFeature, features);
    const moduleCfg = (appConfig.appModules as any)[r.moduleKey] || (appConfig.adminModules as any)[r.moduleKey];
    if (!moduleCfg || moduleCfg.enabled === false) return false;
    return isFeatureAllowed(r.requiresFeature ?? moduleCfg.requiresFeature, features);
  });
}

export function assembleRoutesFromDiscoveredModules(all: DiscoveredRouteModule[], appConfig: AppConfig, features?: Record<string, boolean>): RouteRecordRaw[] {
  const isWhiteLabel = appConfig.projectType === 'white_label';
  const grouped = groupByScope(filterByConfig(all, appConfig, features));

  // Resolve the default landing segment at runtime based on enabled features
  // Falls back to variant default if features are not yet available
  const getLandingSegment = (to?: any): string => {
    try {
      // Import lazily to avoid cyclic init issues; evaluated at navigation time
      const { useFeaturesStore } = require('@/store/features.store');
      const { projectContextManager } = require('@/services/project-context-manager');
      const featuresStore = useFeaturesStore();
      // Resolve projectId quickly for white_label nested redirects
      const routeParams = to && to.params ? to.params : {};
      const paramProjectId = typeof routeParams.projectId === 'string' ? routeParams.projectId : Array.isArray(routeParams.projectId) ? routeParams.projectId?.[0] : undefined;
      const ctxProjectId = projectContextManager?.context?.value?.projectId;
      const projectId = paramProjectId || ctxProjectId || '';

      // Try to hydrate from cache synchronously if features are empty
      try {
        if (projectId && Object.keys((featuresStore as any)?.features || {}).length === 0) {
          if (typeof featuresStore.loadFeaturesFromCache === 'function') {
            featuresStore.loadFeaturesFromCache(projectId);
          }
        }
      } catch {}

      const featuresObj = ((featuresStore as any)?.features || {}) as Record<string, boolean>;
      const keys = Object.keys(featuresObj);
      if (Object.prototype.hasOwnProperty.call(featuresObj, 'home')) {
        return featuresObj.home ? 'home' : 'directory';
      }
      // Heuristic: projects with only a few baseline flags (e.g., directory/login) → directory
      if (keys.length > 0) {
        if (featuresObj.directory === true && keys.length <= 3) return 'directory';
        // Otherwise honor variant default
        return (require('@/config/routing.config') as any).buildAppConfig
          ? (appConfig.routing.defaultRoute || 'home')
          : 'home';
      }
      // No features available yet → honor variant default
      return appConfig.routing.defaultRoute || 'home';
    } catch (e) {
      // Fallback to variant default
      return appConfig.routing.defaultRoute || 'home';
    }
  };

  // Convert discovered into RouteRecordRaw
  const rootRoutes: RouteRecordRaw[] = grouped.root.map(r => {
    const isRootPath = r.path === '/';
    const computedRedirect = isRootPath
      ? (isWhiteLabel
          ? '/projects'
          : ((to: any) => `/${getLandingSegment(to)}`))
      : r.redirect;
    return ({
      path: r.path,
      name: r.name,
      component: r.component,
      redirect: computedRedirect,
      children: r.children ?? [],
      meta: r.meta,
    });
  });

  const appChildren: RouteRecordRaw[] = grouped.app
    .sort((a, b) => (a.order || 100) - (b.order || 100))
    .map(r => ({
      path: r.path,
      name: r.name,
      component: r.component,
      redirect: r.redirect,
      children: r.children ?? [],
      // Propagate feature gates except for core modules that should always be available
      meta: {
        ...(r.meta || {}),
        ...(r.moduleKey === 'directory' ? {} : { requiresFeature: (r as any).requiresFeature, checkIfLive: (r as any).checkIfLive })
      }
    }));

  const adminChildren: RouteRecordRaw[] = grouped.admin
    .sort((a, b) => (a.order || 100) - (b.order || 100))
    .map(r => ({
      path: r.path,
      name: r.name,
      component: r.component,
      redirect: r.redirect,
      children: r.children ?? [],
      meta: { ...(r.meta || {}), requiresFeature: (r as any).requiresFeature, checkIfLive: (r as any).checkIfLive }
    }));

  const assembled: RouteRecordRaw[] = [...rootRoutes];

  if (isWhiteLabel) {
    // Normalize tenant root: '/:projectId' → '/:projectId/' so both work consistently
    assembled.push({
      path: '/:projectId',
      name: 'project-wrapper-no-slash-redirect',
      redirect: (to) => ({ path: `/${to.params.projectId}/` })
    });

    assembled.push({
      path: '/:projectId/',
      component: () => import('@/views/AppView.vue'),
      name: 'project-wrapper',
      meta: {
        wrapperType: 'project',
        requiresAuth: true,
        dataLoaders: ['profile_initialization', 'project_document', 'features']
      },
      children: [
        // When navigating to "/:projectId/", redirect to the configured default route
        {
          path: '',
          name: 'project-index-redirect',
          redirect: (to) => ({ path: `/${to.params.projectId}/${getLandingSegment(to)}` })
        },
        ...appChildren,
        {
          path: 'admin',
          component: () => import('@/views/AdminView.vue'),
          name: 'admin-wrapper',
          meta: { requiresAdmin: true },
          redirect: (to) => {
            // Check if AdminIndex exists in children
            const adminIndex = adminChildren.find(child => child.name === 'AdminIndex');
            
            if (import.meta.env.DEV) {
              console.log('[Router] Admin redirect check:', { 
                path: to.path,
                adminIndexFound: !!adminIndex,
                childrenCount: adminChildren.length,
                firstChild: adminChildren[0]?.name,
                allChildren: adminChildren.map(c => c.name)
              });
            }

            if (adminIndex) {
              return { name: 'AdminIndex', params: to.params };
            }
            // Fallback to first admin child if AdminIndex doesn't exist
            if (adminChildren.length > 0) {
              return { name: adminChildren[0].name, params: to.params };
            }
            return { path: to.path };
          },
          children: adminChildren
        },
        // Catch-all inside project wrapper to properly render 404 within layout
        {
          path: ':pathMatch(.*)*',
          name: 'project-not-found',
          redirect: '/not-found',
          meta: { isErrorPage: true, requiresAuth: false, skipProjectCheck: true, skipPermissionCheck: true }
        }
      ]
    });
  } else {
    // For private_label, wrap all app routes under AppView.vue so layout/loading work
    assembled.push({
      path: '/',
      component: () => import('@/views/AppView.vue'),
      name: 'app-wrapper',
      meta: {
        wrapperType: 'project',
        requiresAuth: true,
        // Foundation data loaders similar to white_label wrapper
        dataLoaders: ['profile_initialization', 'project_document', 'features']
      },
      children: [
        {
          path: '',
          name: 'root-redirect',
          redirect: () => '/' + getLandingSegment()
        },
        // Keep children relative; also ensure requiresAuth on app routes and propagate feature meta
        ...appChildren.map(r => ({ ...r, path: r.path, meta: { ...(r.meta || {}), requiresAuth: true } })),
        {
          path: 'admin',
          component: () => import('@/views/AdminView.vue'),
          name: 'admin-wrapper',
          meta: { requiresAdmin: true },
          redirect: (to) => {
            // Check if AdminIndex exists in children
            const adminIndex = adminChildren.find(child => child.name === 'AdminIndex');
            if (adminIndex) {
              return { name: 'AdminIndex' };
            }
            // Fallback to first admin child if AdminIndex doesn't exist
            if (adminChildren.length > 0) {
              return { name: adminChildren[0].name };
            }
            return { path: to.path };
          },
          children: adminChildren.map(r => ({ ...r, path: r.path }))
        },
        // Catch-all inside app wrapper for private_label
        {
          path: ':pathMatch(.*)*',
          name: 'app-not-found',
          redirect: '/not-found',
          meta: { isErrorPage: true, requiresAuth: false, skipProjectCheck: true, skipPermissionCheck: true }
        }
      ]
    });
  }

  // Global catch-all as final safety net
  assembled.push({
    path: '/:pathMatch(.*)*',
    name: 'catch-all-not-found',
    redirect: '/not-found',
    meta: { isErrorPage: true, requiresAuth: false, skipProjectCheck: true, skipPermissionCheck: true }
  });

  return assembled;
}


